import type { CreateUserInput, UpdateUserInput } from '@crm/shared';
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PROFILES_QUERY_KEYS } from '@/modules/profiles/constants';
import { USERS_QUERY_KEYS, type UserStatusFilter } from '../constants';
import { usersService } from '../services';

/** Mexer em usuario muda a contagem de membros dos grupos - invalida os dois. */
function invalidateUsersAndProfiles(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEYS.all }),
    queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEYS.all }),
  ]);
}

export function useUsers(params: {
  page: number;
  perPage: number;
  search: string;
  status: UserStatusFilter;
}) {
  return useQuery({
    queryKey: USERS_QUERY_KEYS.list(params.page, params.perPage, params.search, params.status),
    queryFn: () => usersService.list(params),
    placeholderData: (previous) => previous,
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: USERS_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => usersService.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateUserInput) => usersService.create(input),
    onSuccess: () => invalidateUsersAndProfiles(queryClient),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      usersService.update(id, input),
    onSuccess: () => invalidateUsersAndProfiles(queryClient),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersService.remove(id),
    onSuccess: () => invalidateUsersAndProfiles(queryClient),
  });
}
